import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutGrid, Upload, Link2, CalendarClock, Clock, Eye, FileMusic, LogOut, Youtube, Plus, X, Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { uploadFile } from "../lib/upload";
import { useSoundCloud, ConnectedAccounts, SoundCloudRow } from "../components/SoundCloud";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Brand";
import { Spinner, EmptyState, StatusBadge } from "../components/states";
import { toast } from "sonner";

const WORKFLOW = ["draft", "submitted", "review", "processing", "published"];

function Stat({ label, value }) {
  return (
    <div className="card-copper rounded-xl p-4">
      <p className="mono-label text-[#71717A]">{label}</p>
      <p className="font-display text-3xl font-extrabold mt-1">{value}</p>
    </div>
  );
}

export default function DjPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dashboard");
  const [modal, setModal] = useState(null); // 'upload'|'external'|'booking'
  const [sc, reloadSc] = useSoundCloud();
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/dj/dashboard").then((r) => { setD(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const r = params.get("sc");
    if (r) { toast[r === "connected" ? "success" : "error"](r === "connected" ? "SoundCloud connected" : r === "denied" ? "SoundCloud authorization was denied" : "SoundCloud connection failed"); setParams({}); reloadSc(); }
  }, [params, setParams, reloadSc]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0C]"><Spinner label="Loading DJ portal" /></div>;
  if (!d) return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center"><EmptyState title="Could not load portal" /></div>;

  const approved = d.dj_status === "approved";

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F4F4F5]">
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo size={38} showText={false} /><div><p className="font-display font-bold">DJ PORTAL</p><p className="mono-label text-[#71717A]">{user?.stage_name || user?.name}</p></div></div>
          <div className="flex items-center gap-3">
            <StatusBadge status={d.dj_status} testid="dj-status-badge" />
            <button data-testid="dj-logout" onClick={() => { logout(); nav("/"); }} className="mono-label text-[#71717A] hover:text-[#E8552A] flex items-center gap-1"><LogOut size={14} /> LOGOUT</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
        {!approved && (
          <div className="card-copper rounded-xl p-4 mb-6 border-[#FF9F1C]/40">
            <p className="mono-label text-[#FF9F1C]">ACCOUNT PENDING APPROVAL</p>
            <p className="text-sm text-[#A1A1AA] mt-1">An admin must approve your DJ account before you can submit recordings. You can still save drafts.</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Total" value={d.stats.total} />
          <Stat label="Published" value={d.stats.published} />
          <Stat label="Pending" value={d.stats.pending} />
          <Stat label="Drafts" value={d.stats.drafts} />
          <Stat label="Rejected" value={d.stats.rejected} />
          <Stat label="Total Views" value={d.stats.total_views} />
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <button data-testid="submit-upload-btn" onClick={() => setModal("upload")} className="copper-btn rounded-full px-5 py-2.5 mono-label flex items-center gap-2"><Upload size={15} /> UPLOAD FILE</button>
          <button data-testid="submit-external-btn" onClick={() => setModal("external")} className="border border-[#E8552A]/40 text-[#E8552A] rounded-full px-5 py-2.5 mono-label flex items-center gap-2 hover:bg-[#E8552A]/10"><Link2 size={15} /> EXTERNAL URL</button>
          <button data-testid="request-slot-btn" onClick={() => setModal("booking")} className="border border-[#27272A] rounded-full px-5 py-2.5 mono-label flex items-center gap-2 hover:border-[#E8552A]"><CalendarClock size={15} /> REQUEST STUDIO SLOT</button>
        </div>

        <div className="mt-8"><ConnectedAccounts sc={sc} reload={reloadSc} /></div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <section>
            <h2 className="font-display text-xl font-bold uppercase flex items-center gap-2 mb-4"><FileMusic size={18} className="text-[#E8552A]" /> Recordings</h2>
            {d.recordings.length === 0 ? <EmptyState title="No recordings yet" sub="Submit your first set using the buttons above." /> : (
              <div className="space-y-3">
                {d.recordings.map((r) => (
                  <div key={r.id} data-testid={`dj-recording-${r.id}`} className="card-copper rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0"><p className="font-display font-semibold truncate">{r.title}</p><p className="mono-label text-[#71717A] mt-0.5">{(r.genres || []).join(" · ")}</p></div>
                      <StatusBadge status={r.status} />
                    </div>
                    {/* Workflow tracker */}
                    <div className="flex items-center gap-1 mt-3">
                      {WORKFLOW.map((s, i) => {
                        const idx = WORKFLOW.indexOf(r.status === "changes" || r.status === "rejected" ? "submitted" : r.status);
                        const active = i <= idx;
                        return <div key={s} className={`h-1 flex-1 rounded ${active ? "bg-[#E8552A]" : "bg-[#27272A]"}`} title={s} />;
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="flex items-center gap-3 mono-label text-[#71717A]"><span className="flex items-center gap-1"><Eye size={12} /> {r.views}</span><span className="flex items-center gap-1" title={r.youtube_error || ""}><Youtube size={12} /> {r.youtube_status?.replace("_", " ")}</span></span>
                      {r.moderation_notes && <span className="mono-label text-[#FF9F1C] truncate max-w-[50%]">{r.moderation_notes}</span>}
                    </div>
                    <SoundCloudRow rec={r} sc={sc} reload={load} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-display text-xl font-bold uppercase flex items-center gap-2 mb-4"><CalendarClock size={18} className="text-[#E8552A]" /> Studio Sessions</h2>
            {d.bookings.length === 0 ? <EmptyState title="No sessions booked" sub="Request a studio slot above." /> : (
              <div className="space-y-3">
                {d.bookings.map((b) => (
                  <div key={b.id} data-testid={`dj-booking-${b.id}`} className="card-copper rounded-xl p-4">
                    <div className="flex items-center justify-between"><p className="font-display font-semibold">{b.recording_type} · {b.genre}</p><StatusBadge status={b.status} /></div>
                    <p className="mono-label text-[#71717A] mt-2 flex items-center gap-2"><Clock size={12} /> {b.preferred_date} · {b.preferred_time} · {b.duration}</p>
                    <p className="text-sm text-[#A1A1AA] mt-1">{b.description}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {modal && <SubmitModal type={modal} approved={approved} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

export function SubmitModal({ type, approved, onClose, onDone }) {
  const [f, setF] = useState({ title: "", genres: "", description: "", external_url: "", media_url: "", cover_image: "", artist_info: "", date: "", preferred_date: "", preferred_time: "", duration: "2h", recording_type: "Live Set", genre: "", special_requirements: "", is_premium: false });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const aiDescribe = async () => {
    if (!f.title) { toast.error("Add a title first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/describe", { title: f.title, genres: f.genres.split(",").map((x) => x.trim()).filter(Boolean), artist_info: f.artist_info, notes: f.description, submission_type: type });
      setF((p) => ({ ...p, description: data.description, genres: p.genres || (data.tags || []).slice(0, 4).join(", ") }));
      toast.success("Description drafted by Claude");
    } catch (e) { toast.error(e.response?.data?.detail || "AI unavailable"); }
    setAiBusy(false);
  };

  const submit = async (asDraft = false) => {
    setBusy(true);
    try {
      if (type === "booking") {
        await api.post("/dj/bookings", { preferred_date: f.preferred_date, preferred_time: f.preferred_time, duration: f.duration, recording_type: f.recording_type, genre: f.genre, description: f.description, special_requirements: f.special_requirements });
        toast.success("Studio slot requested — awaiting admin confirmation");
      } else {
        let media_url = f.media_url, media_file_id = null;
        if (type === "upload") {
          if (!file) throw new Error("Select an audio/video file");
          const up = await uploadFile(file, "media", setProgress);
          media_url = up.url; media_file_id = up.file_id;
        }
        await api.post("/dj/recordings", {
          title: f.title, submission_type: type, genres: f.genres.split(",").map((x) => x.trim()).filter(Boolean),
          description: f.description, external_url: f.external_url, media_url, media_file_id,
          cover_image: f.cover_image, artist_info: f.artist_info, date: f.date, is_premium: f.is_premium, as_draft: asDraft,
        });
        toast.success(asDraft ? "Saved as draft" : "Submitted for admin review");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    setBusy(false); setProgress(0);
  };

  const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A]";
  const titles = { upload: "Upload Audio / Video", external: "External Stream Submission", booking: "Request Studio Slot" };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div data-testid="recording-submission-form" onClick={(e) => e.stopPropagation()} className="bg-[#121216] border border-[#27272A] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-6 fade-up">
        <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold uppercase">{titles[type]}</h3><button data-testid="modal-close" onClick={onClose} className="text-[#71717A] hover:text-[#E8552A]"><X size={20} /></button></div>
        <div className="space-y-3">
          {type === "booking" ? (
            <>
              <input data-testid="booking-date" type="date" value={f.preferred_date} onChange={set("preferred_date")} className={inp} />
              <input data-testid="booking-time" type="time" value={f.preferred_time} onChange={set("preferred_time")} className={inp} />
              <div className="grid grid-cols-2 gap-3">
                <select value={f.duration} onChange={set("duration")} className={inp}><option>1h</option><option>2h</option><option>3h</option></select>
                <select value={f.recording_type} onChange={set("recording_type")} className={inp}><option>Live Set</option><option>Hybrid</option><option>B2B</option><option>Live Act</option></select>
              </div>
              <input data-testid="booking-genre" value={f.genre} onChange={set("genre")} placeholder="Genre" className={inp} />
              <textarea data-testid="booking-desc" value={f.description} onChange={set("description")} placeholder="Description" rows={2} className={inp} />
              <textarea value={f.special_requirements} onChange={set("special_requirements")} placeholder="Special requirements" rows={2} className={inp} />
              <button data-testid="booking-submit" disabled={busy} onClick={() => submit()} className="w-full copper-btn rounded-lg py-3 mono-label disabled:opacity-60">{busy ? "SENDING…" : "REQUEST SLOT"}</button>
            </>
          ) : (
            <>
              <input data-testid="rec-title" value={f.title} onChange={set("title")} placeholder="Title" className={inp} />
              {type === "external" && <input data-testid="rec-url" value={f.external_url} onChange={set("external_url")} placeholder="Video / stream URL" className={inp} />}
              {type === "upload" && (
                <div className="border border-dashed border-[#27272A] rounded-lg p-6 text-center">
                  <Upload className="mx-auto text-[#E8552A]" size={22} />
                  <p className="text-sm text-[#A1A1AA] mt-2">Select audio/video (MP4, MOV, WebM, MP3, WAV, AAC, FLAC)</p>
                  <input data-testid="rec-file" type="file" accept=".mp4,.mov,.webm,.mp3,.wav,.aac,.m4a,.flac" onChange={(e) => setFile(e.target.files?.[0] || null)} className={`${inp} mt-3 file:mr-3 file:bg-[#E8552A] file:text-black file:border-0 file:rounded file:px-3 file:py-1 file:mono-label`} />
                  {file && <p className="mono-label text-[#71717A] mt-2">{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>}
                  {progress > 0 && <div data-testid="upload-progress" className="h-1.5 bg-[#27272A] rounded mt-3 overflow-hidden"><div className="h-full bg-[#E8552A] transition-all" style={{ width: `${progress}%` }} /></div>}
                </div>
              )}
              <input data-testid="rec-genres" value={f.genres} onChange={set("genres")} placeholder="Genres (comma separated)" className={inp} />
              <input data-testid="rec-cover" value={f.cover_image} onChange={set("cover_image")} placeholder="Cover image URL" className={inp} />
              <div className="relative">
                <textarea data-testid="rec-desc" value={f.description} onChange={set("description")} placeholder="Description" rows={3} className={inp} />
                <button type="button" data-testid="ai-describe" disabled={aiBusy} onClick={aiDescribe} className="absolute right-2 bottom-2 mono-label text-[10px] border border-[#E8552A]/50 text-[#E8552A] rounded-full px-3 py-1 hover:bg-[#E8552A]/10 disabled:opacity-50 flex items-center gap-1"><Sparkles size={11} /> {aiBusy ? "WRITING…" : "AI DESCRIBE"}</button>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA]"><input data-testid="rec-premium" type="checkbox" checked={f.is_premium} onChange={(e) => setF({ ...f, is_premium: e.target.checked })} className="accent-[#E8552A]" /> Premium-members-only set</label>
              <div className="flex gap-3">
                <button data-testid="rec-draft" disabled={busy} onClick={() => submit(true)} className="flex-1 border border-[#27272A] rounded-lg py-3 mono-label hover:border-[#E8552A]">SAVE DRAFT</button>
                <button data-testid="rec-submit" disabled={busy || !approved} title={!approved ? "Account must be approved" : ""} onClick={() => submit(false)} className="flex-1 copper-btn rounded-lg py-3 mono-label disabled:opacity-50">{busy ? "…" : "SUBMIT"}</button>
              </div>
              {!approved && <p className="mono-label text-[#FF9F1C] text-center">Approval required to submit — drafts allowed</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
